import type { Transition } from 'motion/react'
import { AnimatePresence, motion } from 'motion/react'
import { use, createContext, useEffect, useState } from 'react'

import { cn } from '~/utils/classnames'

const ASIDE_TRANSITION = {
  in: { duration: 0.35, ease: [0.65, 0, 0.6, 1] } as Transition,
  out: { duration: 0.35, ease: [0.1, 0.5, 0.1, 1] } as Transition,
}

const TOPBAR_TRANSITION = {
  in: {
    duration: ASIDE_TRANSITION.in.duration,
    ease: [0.8, 0, 0.6, 1],
    delay: 0.05,
  } as Transition,
  out: {
    duration: ASIDE_TRANSITION.out.duration,
    ease: [0.1, 0.5, 0.1, 1],
  } as Transition,
}

const TRIGGER_TRANSITION = {
  in: { duration: 0.3, ease: [0.4, 0, 0.7, 1] } as Transition,
  out: { duration: 0.3, ease: ASIDE_TRANSITION.out.ease } as Transition,
}

interface LayoutContextType {
  isLeftOpen: boolean
  isRightOpen: boolean
}

export const LayoutContext = createContext<LayoutContextType>({
  isLeftOpen: false,
  isRightOpen: false,
})

type LayoutRootProps = React.ComponentPropsWithoutRef<'div'> & LayoutContextType

const LayoutRoot = ({
  isLeftOpen,
  isRightOpen,
  className,
  children,
  ...props
}: LayoutRootProps) => {
  const [currentSide, setCurrentSide] = useState<'left' | 'right'>('left')

  useEffect(() => {
    if (isLeftOpen) setCurrentSide('left')
    if (isRightOpen) setCurrentSide('right')
  }, [isLeftOpen, isRightOpen])

  return (
    <LayoutContext value={{ isLeftOpen, isRightOpen }}>
      <div
        className={cn(
          'grid h-full w-full grid-cols-[auto_1fr_auto] grid-rows-[auto_1fr] gap-y-2 contain-paint',
          currentSide === 'right' && 'justify-end',
          className
        )}
        {...props}
      >
        {children}
      </div>
    </LayoutContext>
  )
}

const TopBar = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof motion.div>) => {
  const { isLeftOpen, isRightOpen } = use(LayoutContext)
  const isOpen = !(isLeftOpen || isRightOpen)

  const animate = { height: isOpen ? 'var(--top-bar-height)' : 0 }

  return (
    <motion.div
      className={cn(
        'relative col-2 row-1 shrink-0 overflow-hidden md:col-[1/-1]',
        className
      )}
      initial={animate}
      animate={animate}
      transition={isOpen ? TOPBAR_TRANSITION.out : TOPBAR_TRANSITION.in}
      {...props}
    >
      {children}
    </motion.div>
  )
}

interface AsideContext {
  isOpen: boolean
  side: 'left' | 'right'
}

const AsideContext = createContext<AsideContext | null>(null)

const useAsideContext = () => {
  const context = use(AsideContext)

  if (!context) {
    throw new Error('useAsideContext must be used within an Aside component')
  }

  return context
}

type AsideProps = React.ComponentPropsWithoutRef<typeof motion.div> & {
  side: 'left' | 'right'
}

const Aside = ({ side, className, children, ...props }: AsideProps) => {
  const context = use(LayoutContext)
  const isOpen = side === 'left' ? context.isLeftOpen : context.isRightOpen

  const margin = side === 'left' ? 'marginLeft' : 'marginRight'
  const animate = isOpen ? { [margin]: 0 } : { [margin]: -288 }

  return (
    <AsideContext value={{ isOpen, side }}>
      <motion.div
        className={cn(
          'relative top-0 row-[1/-1] h-full w-(--aside-width) contain-[size,style]',
          '',
          side === 'left' ? 'col-1' : 'col-3',
          className
        )}
        initial={animate}
        animate={animate}
        transition={isOpen ? ASIDE_TRANSITION.in : ASIDE_TRANSITION.out}
        {...props}
      >
        {children}
      </motion.div>
    </AsideContext>
  )
}

const AsideTrigger = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof motion.span>) => {
  const { isOpen, side } = useAsideContext()
  const animate = { x: isOpen ? 0 : side === 'left' ? '100%' : '-100%' }

  return (
    <motion.span
      className={cn(
        'text-foreground absolute top-4 z-10',
        side === 'left' && 'right-1 pl-6',
        side === 'right' && 'left-1 pr-6',
        className
      )}
      initial={animate}
      animate={animate}
      transition={isOpen ? TRIGGER_TRANSITION.in : TRIGGER_TRANSITION.out}
      {...props}
    >
      {children}
    </motion.span>
  )
}

// conditional rendering of content inside the Aside
const AsideConditionalContent = ({
  className,
  children,
}: React.ComponentPropsWithoutRef<'div'>) => {
  const { isOpen } = useAsideContext()

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={className}
          // somewhat hacky way to delay unmount based on the parent Aside animation
          exit={{ pointerEvents: 'none' }}
          transition={{
            delay: isOpen
              ? ASIDE_TRANSITION.in.duration
              : ASIDE_TRANSITION.out.duration,
            duration: 0,
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const Main = ({
  className,
  children,
  ...props
}: React.ComponentPropsWithoutRef<'main'>) => {
  return (
    <main
      className={cn(
        'max-xs:min-w-[calc(100vw-(--spacing(4)))] relative z-10 col-2 row-2 h-full w-full overflow-y-auto rounded-t-2xl',
        '[clip-path:inset(0_0_0_0_round_0.75rem_0.75rem_0_0)]', // fixes chrome bug where sticky elements escape rounded corners
        'w-[calc(100vw-(--spacing(4))] sm:mx-2',
        className
      )}
      {...props}
    >
      {children}
    </main>
  )
}

export const Layout = Object.assign(LayoutRoot, {
  TopBar,
  Aside,
  AsideTrigger,
  AsideConditionalContent,
  Main,
})
